import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * <quantity-selector-cart>
 *
 * Quantity selector for cart items. Displays current quantity with +/- buttons.
 * Used in cart drawer and cart page contexts where each line item needs
 * independent quantity controls.
 *
 * Properties:
 *   quantity  — Current quantity to display
 *   atLimit   — Whether purchase limit is reached (disables + button)
 *   disabled  — Overall disable state (optional)
 *
 * Events:
 *   quantity-minus — Fired when minus button is clicked
 *   quantity-plus  — Fired when plus button is clicked
 *
 * Usage:
 *   <quantity-selector-cart
 *     .quantity=${qty}
 *     .atLimit=${atLimit}
 *     @quantity-minus=${handleMinus}
 *     @quantity-plus=${handlePlus}
 *   ></quantity-selector-cart>
 */
@customElement("quantity-selector-cart")
export class QuantitySelectorCart extends LitElement {
  @property({ type: Number }) quantity = 1;
  @property({ type: Boolean }) disabled = false;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  private _handleMinus() {
    if (this.disabled || this.quantity <= 1) return;
    this.dispatchEvent(new CustomEvent("quantity-minus"));
  }

  private _handlePlus() {
    if (this.disabled) return;
    this.dispatchEvent(new CustomEvent("quantity-plus"));
  }

  render() {
    const buttonClass =
      "shrink-0 w-[25px] h-[25px] flex items-center justify-center rounded-[9999px] border border-[var(--color-neutral-500)] bg-transparent p-0 cursor-pointer [&_svg]:w-3 [&_svg]:h-3 [&_svg]:pointer-events-none";
    const isMinusDisabled = this.disabled || this.quantity <= 1;
    return html`
      <div class="quantity relative flex items-center gap-2">
        <button
          class="${buttonClass}${isMinusDisabled
            ? " opacity-50 cursor-not-allowed pointer-events-none"
            : ""}"
          type="button"
          ?disabled=${isMinusDisabled}
          @click=${this._handleMinus}
          aria-label="Decrease quantity"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 10 2"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z"
              fill="currentColor"
            />
          </svg>
        </button>
        <div class="flex-1 min-w-0 text-center font-medium">
          ${this.quantity}
        </div>
        <button
          class="${buttonClass}"
          type="button"
          @click=${this._handlePlus}
          aria-label="Increase quantity"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
            viewBox="0 0 10 10"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "quantity-selector-cart": QuantitySelectorCart;
  }
}
